"use client";

import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { CityInfo } from "modules/cards/model/card";
import { QUERIES } from "modules/common/query";
import { clientLogger } from "modules/common/logger/client";

interface ReverseGeocodeResult {
  cityName: string;
  city: string;
  country: string;
}

interface CityWithGeocode extends CityInfo {
  cityName?: string;
  city?: string;
  country?: string;
  isLoading: boolean;
}

function getCoordinateKey(latitude: number, longitude: number): string {
  return `${latitude.toFixed(6)},${longitude.toFixed(6)}`;
}

/**
 * Aggregate cities into a map by key. Counts use `Math.max` rather than
 * summing — two `CityInfo` entries that collapse to the same key represent
 * different 0.1° cells of the same physical city, and the same trade item
 * (one user, multiple stores in that city) is already counted in each cell.
 * Summing would over-count; max is a safe lower bound.
 */
function aggregateCities<T extends CityWithGeocode>(
  cities: T[],
  getKey: (city: T) => string
): Map<string, T> {
  const map = new Map<string, T>();

  for (const city of cities) {
    const key = getKey(city);
    const existing = map.get(key);

    if (existing) {
      existing.availableCount = Math.max(
        existing.availableCount,
        city.availableCount
      );
      existing.wantedCount = Math.max(
        existing.wantedCount,
        city.wantedCount
      );
      existing.storeIds = [
        ...new Set([...existing.storeIds, ...city.storeIds]),
      ];
      existing.storeCount = existing.storeIds.length;
    } else {
      map.set(key, { ...city });
    }
  }

  return map;
}

/**
 * Client-side reverse geocoding that calls our internal API route.
 * This avoids CORS issues and middleware overhead from server actions.
 */
async function clientReverseGeocode(
  lat: number,
  lng: number
): Promise<ReverseGeocodeResult | null> {
  // Validate coordinates
  if (
    isNaN(lat) ||
    isNaN(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return null;
  }

  // Use internal API route to avoid CORS and leverage server-side caching
  const url = `/api/geocoding/reverse?lat=${lat}&lng=${lng}`;

  const res = await fetch(url);

  if (!res.ok) {
    clientLogger.error(
      "useClientReverseGeocode",
      "reverse geocode returned non-OK",
      new Error(`status=${res.status} ${res.statusText}`),
      { status: res.status }
    );
    return null;
  }

  const data = await res.json();

  if (!data || !data.city) {
    return null;
  }

  return data as ReverseGeocodeResult;
}

/**
 * Hook for reverse geocoding cities using direct client-side API calls.
 * Uses parallel fetching for better performance.
 */
export function useClientReverseGeocode(cities: CityInfo[]): CityWithGeocode[] {
  const queries = useQueries({
    queries: cities.map((city) => ({
      queryKey: [QUERIES.REVERSE_GEOCODE, city.latitude, city.longitude],
      queryFn: () => clientReverseGeocode(city.latitude, city.longitude),
      staleTime: 24 * 60 * 60 * 1000, // 24 hours
      gcTime: 24 * 60 * 60 * 1000, // 24 hours
      retry: 2,
      retryDelay: 1000,
    })),
  });

  return useMemo(() => {
    const enriched: CityWithGeocode[] = cities.map((city, index) => {
      const query = queries[index];
      const fallbackCityName = `${city.latitude.toFixed(2)}, ${city.longitude.toFixed(2)}`;
      return {
        ...city,
        cityName:
          query.data?.cityName ||
          (query.isError ? fallbackCityName : undefined),
        city: query.data?.city,
        country: query.data?.country,
        isLoading: query.isLoading && !query.isError,
      };
    });

    // Split into resolved and pending cities
    const resolved = enriched.filter((c) => !c.isLoading && c.cityName);
    const pending = enriched.filter((c) => c.isLoading || !c.cityName);

    // Deduplicate resolved cities by cityName, pending cities by coordinates
    const resolvedMap = aggregateCities(resolved, (c) => c.cityName!);
    const pendingMap = aggregateCities(pending, (c) =>
      getCoordinateKey(c.latitude, c.longitude)
    );

    return [...resolvedMap.values(), ...pendingMap.values()];
  }, [cities, queries]);
}
